'use client';

/**
 * Vendor Portal Explorer
 *
 * Lists RFQs available to the current vendor via the data gateway.
 * Uses vendor_portal_rfq_list strategy — actor_sid injected server-side from JWT.
 *
 * @component_role Vendor RFQ Navigation List
 * @business_rule Vendors only see RFQs they are invited to via LINKED_TO relationship.
 *   Access is enforced at the Cypher level, not the frontend.
 * @user_outcome Vendors see all RFQs they are invited to quote on,
 *   with deadline and status at a glance, and can select one to open
 *   the quote submission form.
 * @integration_point klyde_edge data gateway - vendor_portal_rfq_list strategy
 *
 * @layer presentation
 * @arch_component_type explorer
 * @arch_domain vendor_portal
 *
 * @component_type Explorer Panel
 * @domain Vendor Portal
 */

import { useEffect, useState } from 'react';
import { FileText, Loader2, Clock, AlertCircle, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import type { VPRfqSummary, VPRfqListResponse } from '@/lib/api/types';

export function VendorPortalExplorer() {
  const selectedUser = useDevUserStore((s) => s.selectedUser);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const listStrategyName = useCapabilityStore((s) => s.strategyName('vendor_portal', 'list'));

  const [rfqs, setRfqs] = useState<VPRfqSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refetch when user switches — actor_sid injected server-side from JWT
  useEffect(() => {
    if (!selectedUser || !listStrategyName) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    api
      .getStrategyData<VPRfqListResponse>(listStrategyName!, {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load RFQs');
          return;
        }
        setRfqs(res.data.rfqs ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedUser, listStrategyName]);

  if (!listStrategyName) {
    return (
      <div className="p-4 text-sm text-muted-foreground text-center">
        <ShieldAlert className="w-4 h-4 mx-auto mb-2 opacity-50" />
        RFQ list not available for your role
      </div>
    );
  }

  if (!selectedUser) {
    return (
      <div className="p-4 text-sm text-muted-foreground text-center">
        <AlertCircle className="w-4 h-4 mx-auto mb-2 opacity-50" />
        Select a dev user from the toolbar
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive text-center">{error}</div>
    );
  }

  if (rfqs.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground text-center">
        No RFQs assigned to this vendor
      </div>
    );
  }

  return (
    <div className="py-1">
      {rfqs.map((rfq) => {
        const isSelected = selectedNode?.id === rfq.rfq_sid;
        return (
          <button
            key={rfq.rfq_sid}
            onClick={() =>
              selectNode({
                id: rfq.rfq_sid,
                label: rfq.reference,
                type: 'rfq',
              })
            }
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
              'hover:bg-accent/50 transition-colors border-b border-border/30',
              isSelected && 'bg-accent'
            )}
          >
            <FileText className="w-4 h-4 shrink-0 text-yellow-400" />
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium">{rfq.reference}</div>
              <div className="text-xs text-muted-foreground truncate">
                {rfq.project_name ?? 'No project'}
                {rfq.line_count > 0 && ` · ${rfq.line_count} items`}
              </div>
            </div>
            <div className="shrink-0 flex items-center gap-2">
              {rfq.deadline && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {new Date(rfq.deadline).toLocaleDateString()}
                </span>
              )}
              <span
                className={cn(
                  'text-xs px-1.5 py-0.5 rounded',
                  rfq.state === 'sent' && 'bg-blue-500/20 text-blue-500',
                  rfq.state === 'draft' && 'bg-gray-500/20 text-gray-500',
                  rfq.state === 'done' && 'bg-green-500/20 text-green-500'
                )}
              >
                {rfq.state}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
